import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { Loader } from 'src/components/Loader';
import { Typography } from 'src/components/Typography';
import { fetchFaqCategoryFaqs } from 'src/models/contentFaq/contentFaq';

import { FaqHtmlContent } from './FaqHtmlContent';
import { FaqCenteredState, FaqWebviewShell } from './FaqWebviewShell';

export default function FaqCategoryPage() {
  const { t } = useTranslation('translation', { keyPrefix: 'appWebview.faq' });
  const { categoryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { data, error, isError, isLoading } = useQuery({
    enabled: !!categoryId,
    queryKey: ['faq-category', categoryId],
    queryFn: () => fetchFaqCategoryFaqs(categoryId || ''),
  });

  useEffect(() => {
    if (!location.hash || !data?.faqs?.length) {
      return;
    }

    const anchorId = location.hash.slice(1);
    const alignToAnchor = (behavior: ScrollBehavior) => {
      document
        .getElementById(anchorId)
        ?.scrollIntoView({ behavior, block: 'start' });
    };

    alignToAnchor('auto');

    const shortDelayId = window.setTimeout(() => alignToAnchor('auto'), 120);
    const longDelayId = window.setTimeout(() => alignToAnchor('auto'), 450);

    return () => {
      window.clearTimeout(shortDelayId);
      window.clearTimeout(longDelayId);
    };
  }, [data?.faqs?.length, location.hash]);

  if (!categoryId) {
    return (
      <FaqWebviewShell>
        <FaqCenteredState>
          <Typography color="error">
            {t('categoryNotFound', {
              defaultValue: 'FAQ category not found.',
            })}
          </Typography>
        </FaqCenteredState>
      </FaqWebviewShell>
    );
  }

  if (isLoading) {
    return (
      <FaqWebviewShell>
        <FaqCenteredState>
          <Loader
            label={t('loadingFaqs', { defaultValue: 'Loading FAQs...' })}
            size="medium"
            sx={{
              minWidth: 'auto',
              width: 'auto',
            }}
          />
        </FaqCenteredState>
      </FaqWebviewShell>
    );
  }

  if (isError) {
    return (
      <FaqWebviewShell>
        <FaqCenteredState>
          <Typography color="error">
            {error instanceof Error
              ? error.message
              : t('categoryLoadError', {
                  defaultValue: 'Unable to load FAQs for this topic.',
                })}
          </Typography>
        </FaqCenteredState>
      </FaqWebviewShell>
    );
  }

  return (
    <FaqWebviewShell>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography component="h1" fontWeight={700} variant="h6">
            {t('faqForTitle', {
              defaultValue: 'FAQ for {{title}}',
              title: data?.category?.title || '',
            })}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            {data?.faqs?.length ? (
              data.faqs.map((faq) => (
                <Box
                  component="a"
                  href={`#faq-${faq.id}`}
                  key={String(faq.id)}
                  onClick={(event) => {
                    event.preventDefault();
                    const anchorId = `faq-${faq.id}`;

                    navigate({ hash: `#${anchorId}` }, { replace: true });
                    document.getElementById(anchorId)?.scrollIntoView({
                      behavior: 'auto',
                      block: 'start',
                    });
                  }}
                  sx={{
                    alignItems: 'center',
                    color: 'inherit',
                    display: 'flex',
                    gap: 1.5,
                    textDecoration: 'none',
                  }}
                >
                  <Icon color="primary" icon={faCircleQuestion} />
                  <Typography>{faq.title}</Typography>
                </Box>
              ))
            ) : (
              <Typography color="text.secondary">
                {t('emptyCategory', {
                  defaultValue: 'No FAQs available for this topic.',
                })}
              </Typography>
            )}
          </Box>
        </Box>

        {data?.faqs?.map((faq) => (
          <Box
            id={`faq-${faq.id}`}
            key={String(faq.id)}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              mt: 2,
              p: 2,
              scrollMarginTop: 'var(--faqAnchorOffset, 120px)',
            }}
          >
            <Typography component="h2" fontWeight={700} variant="subtitle1">
              {faq.title}
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <FaqHtmlContent html={faq.content} />
            </Box>
          </Box>
        ))}
      </Box>
    </FaqWebviewShell>
  );
}
